<template>
    <nav
        id="topbar"
        :aria-label="$t('admin.Admin topbar')"
        class="topbar">
        <div class="topbar-title">
            <pendo-logo :type="logoType" />
        </div>
        <div class="topbar-actions">
            <div
                v-for="route in routes"
                :key="route.value"
                :class="{ active: isActive(route) }"
                class="action--link">
                <pendo-button
                    theme="app"
                    :label="route.label"
                    type="tertiary"
                    @click="goToRoute(route.name)" />
            </div>
        </div>
        <div
            class="topbar-switchers"
            :class="{ 'products-switcher': userCanAccessClassicFeedback || productSwitcherList.length }">
            <pendo-multiselect
                v-if="subscriptionList.length > 1"
                v-model="subscription"
                :options="subscriptionList"
                :searchable="false"
                :hide-selected-options="true"
                placeholder="Switch Subscription"
                label-key="displayName"
                class="action--sub-switcher sub-switcher"
                @select="switchSubscription">
                <template #selectedLabel="{ option }">
                    <div class="sub-switcher--label">
                        <subscriptions-icon class="topbar-icon" />
                        <span>
                            {{ option.displayName }}
                        </span>
                    </div>
                </template>
            </pendo-multiselect>
            <pendo-multiselect
                v-if="userCanAccessClassicFeedback"
                v-model="productLine"
                hide-selected-options
                label-key="displayName"
                value-key="displayName"
                class="product-line-switcher"
                :options="productSwitcherList"
                @select="goToProductLineRoute">
                <template #trigger="{ comboboxAttrs, handleKeydown, toggleMenu }">
                    <div
                        v-bind="comboboxAttrs"
                        class="product-line-switcher__wrapper"
                        @click="toggleMenu"
                        @keydown="handleKeydown">
                        <product-lines-icon />
                    </div>
                </template>
            </pendo-multiselect>
        </div>
    </nav>
</template>

<script>
import { DEFAULT_PERMISSIONS_REDIRECT } from '@/vue-router/route-permissions.utils';
import { adminRedirect } from '@/vue-router/router';
import { mapGetters, mapState } from 'vuex';
import { PendoButton, PendoMultiselect, PendoLogo } from '@pendo/components';
import { generateFeedbackSSOUrl } from '@utils/sso';
import ProductLinesIcon from './product-lines-icon.vue';
import SubscriptionsIcon from './subscriptions-icon.vue';
import useDarkMode from '@/common/use/use-dark-mode/use-dark-mode.js';

export default {
    name: 'Topbar',
    components: {
        PendoButton,
        PendoMultiselect,
        ProductLinesIcon,
        SubscriptionsIcon,
        PendoLogo,
    },
    setup () {
        const { isPendoDarkMode } = useDarkMode();

        return {
            isPendoDarkMode,
        };
    },
    data () {
        return {
            productLine: {
                displayName: 'Settings',
            },
            allRoutes: [
                {
                    value: '/user-profile',
                    name: 'UserProfile',
                    label: 'Personal Settings',
                },
                {
                    value: '/admin',
                    name: 'Admin',
                    label: 'Subscription Settings',
                },
                {
                    value: '/admin/users',
                    name: 'UsersAdmin',
                    label: 'Users',
                    admin: true,
                },
                {
                    value: '/admin/product-areas',
                    name: 'ProductAreasAdmin',
                    label: 'Product Areas',
                    admin: true,
                },
            ],
        };
    },
    computed: {
        ...mapState({
            subscription: (state) => state.subscription.active,
            hosts: (state) => state.config.hosts,
        }),
        ...mapGetters({
            hasAdminAccess: 'auth/hasAdminAccess',
            userCanAccessClassicFeedback: 'auth/userCanAccessClassicFeedback',
            subscriptions: 'subscription/list',
            isPendoFree: 'subscription/isPendoFree',
            hasProductLinePurchased: 'productLines/hasProductLinePurchased',
        }),
        hasPendoCoreAccess () {
            return this.hasProductLinePurchased('core');
        },
        subscriptionList () {
            return this.subscriptions.filter((subscription) => {
                return !subscription.featureFlags.sampleDataSubscription;
            });
        },
        productSwitcherList () {
            const options = [];
            if (this.hasPendoCoreAccess) {
                options.push({
                    type: 'core',
                    displayName: 'Pendo',
                });
            }

            if (this.userCanAccessClassicFeedback) {
                options.push({
                    type: 'feedback',
                    displayName: 'Feedback (classic)',
                });
            }

            return options;
        },
        routes () {
            return this.allRoutes.filter((route) => {
                if (route.value === '/admin/product-areas') {
                    if (this.isPendoFree) {
                        return false;
                    }

                    if (!this.userCanAccessClassicFeedback) {
                        return false;
                    }
                }

                if (route.value === '/admin') {
                    const { name } = adminRedirect({});

                    return name !== DEFAULT_PERMISSIONS_REDIRECT.name;
                }

                if (route.admin) {
                    return this.hasAdminAccess;
                }

                return true;
            });
        },
        logoType () {
            return this.isPendoDarkMode ? 'night' : 'day';
        },
    },
    methods: {
        async switchSubscription ({ id }) {
            if (id === this.subscription.id) {
                return;
            }

            await this.$router.pendo.switchSubscription(id);
        },
        goToRoute (name) {
            this.$router.push({
                name,
                query: {
                    globalSettings: true,
                },
            });
        },
        isActive (route) {
            return this.$route.name === route.name;
        },
        goToProductLineRoute (productLine) {
            switch (productLine.type) {
                case 'core':
                    this.goToCore();
                    break;
                case 'feedback':
                    this.goToFeedback();
                    break;
            }
        },
        goToFeedback () {
            const url = generateFeedbackSSOUrl('/app', this.hosts);
            window.location.href = url;
        },
        goToCore () {
            window.location.href = window.location.origin + window.location.pathname;
        },
    },
};
</script>

<style scoped lang="less">
.topbar {
    z-index: 2;
    background-color: var(--palette-gray-0);
    height: 64px;
    box-shadow: 0 2px 4px 0 var(--semantic-drop-shadow-base);
    width: 100%;
    display: flex;
    align-items: center;
    gap: 16px;

    &-icon {
        display: block;
        text-decoration: none;
    }

    &-title {
        padding: 0 0 0 3rem;
        display: flex;
        flex-flow: row nowrap;
        align-items: center;
        flex-grow: 1;
    }

    &-actions,
    &-switchers {
        .action--link {
            text-decoration: none;

            :deep(.pendo-button) {
                color: var(--palette-gray-70);
            }

            &.active {
                :deep(.pendo-button) {
                    color: var(--palette-gray-110);
                    font-weight: 600;
                }
            }
        }
    }

    &-actions {
        display: grid;
        grid-auto-flow: column;
        align-items: center;
        grid-gap: 32px;
        padding: 0 16px;
    }

    &-switchers {
        display: grid;
        grid-auto-flow: column;
        grid-gap: 8px;
        align-items: center;
        justify-content: end;
        padding: 0 3rem 0 0;

        &.products-switcher {
            grid-template-columns: auto 36px;
        }
    }
}

.sub-switcher {
    &--label {
        display: flex;
        flex-flow: row nowrap;
        justify-content: flex-start;
        align-items: center;

        svg {
            margin-right: 0.5em;
        }
    }

    &--footer {
        display: flex;
        flex-flow: row nowrap;
        justify-content: center;
        align-items: center;
        border-top: 1px solid var(--palette-gray-40);
        padding-top: 0.75em;
        padding-bottom: 0.75em;
        cursor: pointer;

        &:hover {
            background-color: var(--palette-gray-20);
        }

        &--toggle-text {
            margin-left: 0.5em;
        }
    }
}

.product-line-switcher {
    cursor: pointer;
    border-radius: 3px;

    &__wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 36px;
        width: 36px;
    }

    &:hover {
        background-color: var(--palette-gray-20);
    }
}
</style>
